import React from 'react';
import { Paper, Typography, IconButton, Box, Stack, FormControl, Select, InputLabel, MenuItem, Button } from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import UTurnLeftIcon from '@mui/icons-material/UTurnLeft';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Highlighter from 'react-highlight-words'
// import corpus from '../resources/corpus.json'
import Banner from '../components/Banner'
import { TPageText, TBook } from '../resources/types'
import { CorpusContext } from "../contexts/CorpusContext"
import { grey, lightGreen } from '@mui/material/colors'

function BrowsePage() {
  const navigate = useNavigate()
  const params = useParams()
  const { search } = useLocation()
  const [query] = useSearchParams()
  const [searchTerm, setSearchTerm] = React.useState('')
  // const [corpusData, setCorpusData] = React.useState<TBook[]>([])
  const corpusData = React.useContext(CorpusContext)

  React.useEffect(
    () => {
      const cleaned = query.get('search')?.replace(/\P{Script=Han}/gu, '') ?? ''
      console.log(`original: ${query.get('search')}, cleaned = ${cleaned}`)
      setSearchTerm(cleaned)
    },
    [query]
  )

  const book = React.useMemo(
    () => corpusData.find(book => book.code === params.code),
    [params, corpusData]
  )

  const pageIndex = React.useMemo(
    () => Number(params.page ?? '1') - 1,
    [params]
  )

  const pageContents = React.useMemo(
    () => book?.contents?.[pageIndex]?.text,
    [book, pageIndex]
  )

  const prevPage = React.useMemo(
    () => {
      if (pageIndex > 0) {
        return pageIndex
      } else {
        return null
      }
    },
    [pageIndex]
  )

  const nextPage = React.useMemo(
    () => {
      const pageCount = book?.contents.length ?? 0
      if (book?.contents && pageIndex < pageCount - 1) {
        console.log(`setting nextPage, pageCount = ${pageCount}, nextPage = ${pageIndex + 2}`)
        return pageIndex + 2
      } else {
        return null
      }
    },
    [pageIndex, book]
  )

  const books = React.useMemo(
    () => corpusData.map(b => ({
      code: b.code,
      title: b.title
    })),
    [corpusData]
  )

  const pages = React.useMemo(
    () => book?.contents,
    [book]
  )

  const handleBookChange = (event: SelectChangeEvent) => {
    navigate({
      pathname: `/browse/${event.target.value}/1`,
      search
    })
  }

  const handlePageChange = (event: SelectChangeEvent) => {
    navigate({
      pathname: `/browse/${book?.code}/${event.target.value}`,
      search
    })
  }

  return (
    <Paper sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }} elevation={0}>
      <Banner />
      <Stack sx={{ flex: 1, m: 0, pl: 1, pr: 1, mb: 1 }} display="flex" flexDirection="column">
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Typography variant="h4"><MenuBookIcon fontSize="large" sx={{ verticalAlign: 'middle', mb: 0.5, mr: 1 }} />瀏覽文獻</Typography>
          {!!searchTerm && <Box>
            <Button variant='outlined' startIcon={<UTurnLeftIcon sx={{ transform: 'rotate(90deg)' }} />} onClick={() => navigate({
              pathname: '/search',
              search
            })}>
              返回搜尋
            </Button>
          </Box>}
          {!searchTerm && <Box>
            <Button variant='outlined' startIcon={<SearchIcon />} onClick={() => navigate({
              pathname: '/search'
            })}>
              搜尋文獻
            </Button>
          </Box>}
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" mt={2}>
          <FormControl variant="standard" sx={{ width: book ? '24ch' : '100%' }}>
            <InputLabel id="literature-label">選擇文獻</InputLabel>
            <Select labelId="literature-label" label="選擇文獻" value={params.code} onChange={handleBookChange}>
              {books?.map(b => <MenuItem key={b.code} value={b.code}>{b.title}</MenuItem>)}
            </Select>
          </FormControl>
          {book && <Box display="flex" flexDirection="row" alignItems="center" sx={{ width: '50%' }}>
            <IconButton onClick={() => navigate({ pathname: `/browse/${book?.code}/1`, search })}>
              <FirstPageIcon />
            </IconButton>
            <IconButton disabled={prevPage === null} onClick={() => navigate({ pathname: `/browse/${book?.code}/${prevPage}`, search })}>
              <NavigateBeforeIcon />
            </IconButton>
            <FormControl variant="standard" sx={{ m: 1, width: '24ch' }}>
              <Select value={pageIndex.toString()} onChange={handlePageChange}>
                {pages?.map((p, index) => <MenuItem key={index} value={index}>{`第${index + 1}頁 （頁碼：${p.page}）`}</MenuItem>)}
              </Select>
            </FormControl>
            <IconButton disabled={nextPage === null} onClick={() => {
              console.log(`nextPage: ${nextPage ?? '-'}`)
              navigate({ pathname: `/browse/${book?.code}/${nextPage}`, search })
            }}>
              <NavigateNextIcon />
            </IconButton>
            <IconButton onClick={() => pages && navigate({ pathname: `/browse/${book?.code}/${pages?.length}`, search })}>
              <LastPageIcon />
            </IconButton>
          </Box>}
        </Box>
        {pageContents && <Box sx={{
          flex: 1, mt: 1, p: 1, border: 'solid 1px lightgrey', borderRadius: 2,
          backgroundColor: lightGreen[50],
          fontSize: { sm: 'large', lg: 'x-large' },
          fontFamily: '"Noto Serif HK", serif',
          lineHeight: { sm: '1.5rem', lg: '2rem' },
          letterSpacing: { sm: '0.3rem', lg: '0.6rem' },
          whiteSpace: 'pre-wrap',
          columnCount: { lg: Math.floor((pageContents?.split('\n')?.length ?? 0) / 16) },
          columnFill: 'auto', columnGap: 2, columnRuleStyle: 'dashed', columnRuleWidth: 1, columnRuleColor: grey[400]
        }}>
          <Highlighter searchWords={[searchTerm]} textToHighlight={pageContents ?? ''} />
        </Box>}
      </Stack>
    </Paper>
  );
}


export default BrowsePage;
